/**
 * Item Library Integration for Plot Pavings
 * Maps field names to rates from Item Library API
 * Handles dynamic fields where codes come from user selections
 */

import { ItemLibraryResponse, ItemLibraryItem } from '../api/itemLibrary';
import { PLOT_PAVINGS_REFERENCES } from './plotPavingsReferenceMapping';
import { PLOT_SLABS_FIELD_LABELS, PLOT_SLABS_FIELD_UNITS } from './plotPavingsFieldMapping';
import { EVALUATE_CODES, EvaluateCode } from './constants';

export interface RateInfo {
  rate: number;
  description: string;
  unit: string;
  resourceCode?: string;
}

/**
 * GWALP006A is a m³ rate (sub-base / MOT Type 1 for pavings). For the bill summary
 * we need a m² rate: effective rate = m³ rate × thickness (m).
 * Plot Pavings stores the thickness directly under the field name in plotPavingsDimensionValues.
 */
const M3_RATE_FIELDS = new Set(['pavingSubBase', 'macadamSubBase']);
export const PLOT_PAVINGS_M3_CONVERSION_FIELDS: ReadonlySet<string> = M3_RATE_FIELDS;

function parseThicknessValue(raw: string): number | null {
  const match = raw.trim().match(/^(\d+(?:\.\d+)?)/);
  return match ? parseFloat(match[1]) : null;
}

function getM3ToM2Multiplier(fieldName: string, groundworksData: any): number {
  if (!M3_RATE_FIELDS.has(fieldName)) return 1;
  const raw = groundworksData?.plotPavingsColumnHeaders?.[fieldName] as string | undefined;
  if (!raw) return 1;
  const value = parseThicknessValue(raw);
  if (!value || value <= 0) return 1;
  // value >= 1 → entered as mm (e.g. "150") → convert to metres
  // value < 1  → already metres (e.g. "0.150")
  return value >= 1 ? value / 1000 : value;
}

/**
 * Get rate from item library by resource code
 */
function getRateByCode(
  resourceCode: string,
  resourceCodeMap: Map<string, ItemLibraryItem>,
  fieldName: string,
  fallbackDescription?: string,
): RateInfo {
  const normalizedCode = resourceCode.trim().toUpperCase();
  const item = resourceCodeMap.get(normalizedCode);

  if (item) {
    return {
      rate: item.nettRate || item.baseRate || 0,
      description: item.resourceDescription || fallbackDescription || fieldName,
      unit: item.unit || '',
      resourceCode: normalizedCode,
    };
  }

  // Item not found - use fallback description if provided
  return {
    rate: 0,
    description: fallbackDescription || fieldName,
    unit: '',
    resourceCode: '',
  };
}

/**
 * Create reverse mapping from label to code for EVALUATE_CODES
 */
function createLabelToCodeMapping(): Record<string, string> {
  const labelToCode: Record<string, string> = {};

  Object.entries(EVALUATE_CODES).forEach(([code, info]) => {
    if (info.label) {
      labelToCode[info.label] = code;
    }
  });

  return labelToCode;
}

const LABEL_TO_CODE = createLabelToCodeMapping();

/**
 * Get evaluate code from label
 * @param labelOrCode - Could be either a label ("100mm thick AC 32mm dense bin") or code ("ALP042")
 */
function getCodeFromLabelOrCode(labelOrCode: string): { code: string; description: string } | undefined {
  const trimmed = labelOrCode.trim();

  // Check if it's already a code (starts with ALP or GWALP)
  if (trimmed.startsWith('ALP') || trimmed.startsWith('GWALP')) {
    const evaluateCode = trimmed as EvaluateCode;
    if (EVALUATE_CODES[evaluateCode]) {
      return {
        code: trimmed,
        description: EVALUATE_CODES[evaluateCode].label
      };
    }
  }

  // Otherwise, reverse-lookup the code from the label
  const code = LABEL_TO_CODE[trimmed];
  if (code) {
    return {
      code,
      description: trimmed
    };
  }

  return undefined;
}

/**
 * Transform plotPavingsDimensionValues to semantic field format with code and description
 * @param plotPavingsDimensionValues - Dimension values (semantic field names as keys)
 * @returns Semantic field-based dimension values with code and description
 */
function transformDimensionValuesToSemanticFormat(
  plotPavingsDimensionValues: Record<string, string>
): Record<string, { code: string; description: string }> {
  const semanticDimensions: Record<string, { code: string; description: string }> = {};

  // Plot Pavings uses semantic field names directly (no column mapping needed)
  Object.entries(plotPavingsDimensionValues).forEach(([fieldName, value]) => {
    if (value && typeof value === 'string' && value.trim() !== '') {
      const codeInfo = getCodeFromLabelOrCode(value);
      if (codeInfo) {
        semanticDimensions[fieldName] = codeInfo;
      }
    }
  });

  return semanticDimensions;
}

/**
 * Get dynamic code info for a field from transformed dimension values
 * @param fieldName - Semantic field name (e.g., 'macadamDbBase')
 * @param semanticDimensions - Transformed dimension values in semantic format
 */
function getDynamicCodeInfo(
  fieldName: string,
  semanticDimensions: Record<string, { code: string; description: string }>
): { code: string; description: string } | undefined {
  return semanticDimensions[fieldName];
}

/**
 * Create a rate lookup map from item library for Plot Pavings
 * @param itemLibraryData - Item library response from API
 * @param groundworksData - Groundworks data for dynamic field values (optional)
 */
export function createPlotPavingsRateLookupMap(
  itemLibraryData: ItemLibraryResponse | undefined,
  groundworksData?: any,
): Map<string, RateInfo> {
  const rateMap = new Map<string, RateInfo>();

  if (!itemLibraryData) {
    return rateMap;
  }

  // Create a map of resourceCode to item for quick lookup
  const resourceCodeMap = new Map<string, ItemLibraryItem>();
  itemLibraryData.forEach((item: ItemLibraryItem) => {
    const code = item.resourceCode?.trim().toUpperCase();
    if (code) {
      resourceCodeMap.set(code, item);
    }
  });

  // Transform dimension values from semantic format upfront
  const semanticDimensions = groundworksData?.plotPavingsDimensionValues
    ? transformDimensionValuesToSemanticFormat(groundworksData.plotPavingsDimensionValues)
    : {};

  // Map each field name to its rate info (rate, description, unit)
  Object.entries(PLOT_PAVINGS_REFERENCES).forEach(([fieldName, reference]) => {
    // Get proper label from field mapping as fallback
    const fallbackDescription = PLOT_SLABS_FIELD_LABELS[fieldName] || fieldName;

    // Check for dynamic code first (from user selections)
    const dynamicCodeInfo = getDynamicCodeInfo(fieldName, semanticDimensions);

    if (dynamicCodeInfo) {
      // Use dynamic code and description from user selection
      const rateInfo = getRateByCode(
        dynamicCodeInfo.code,
        resourceCodeMap,
        fieldName,
        dynamicCodeInfo.description
      );
      const m3ToM2 = getM3ToM2Multiplier(fieldName, groundworksData);
      rateMap.set(fieldName, {
        ...rateInfo,
        rate: rateInfo.rate * m3ToM2,
        unit: fieldName in PLOT_SLABS_FIELD_UNITS ? PLOT_SLABS_FIELD_UNITS[fieldName] : rateInfo.unit,
      });
      return;
    }

    if (!reference.code) {
      // Fields without static or dynamic codes
      rateMap.set(fieldName, {
        rate: 0,
        description: fallbackDescription,
        unit: PLOT_SLABS_FIELD_UNITS[fieldName] ?? '',
      });
      return;
    }

    // Static code from reference mapping
    const rateInfo = getRateByCode(reference.code, resourceCodeMap, fieldName, fallbackDescription);
    const m3ToM2 = getM3ToM2Multiplier(fieldName, groundworksData);
    rateMap.set(fieldName, {
      ...rateInfo,
      rate: rateInfo.rate * m3ToM2,
      unit: fieldName in PLOT_SLABS_FIELD_UNITS ? PLOT_SLABS_FIELD_UNITS[fieldName] : rateInfo.unit,
    });
  });

  return rateMap;
}

/**
 * Get rate info for a specific field name
 */
export function getPlotPavingsRateInfo(
  fieldName: string,
  rateLookupMap: Map<string, RateInfo>,
): RateInfo {
  return rateLookupMap.get(fieldName) || { rate: 0, description: fieldName, unit: PLOT_SLABS_FIELD_UNITS[fieldName] ?? '' };
}

/**
 * Apply rates and descriptions to Plot Pavings summary items
 */
export function applyPlotPavingsRatesToSummary(
  summaryItems: any[],
  rateLookupMap: Map<string, RateInfo>,
): any[] {
  return summaryItems.map((item) => {
    const rateInfo = getPlotPavingsRateInfo(item.fieldName, rateLookupMap);
    const amount = Math.abs(item.quantity * rateInfo.rate);

    return {
      ...item,
      rate: rateInfo.rate,
      description: item.description,
      unit: rateInfo.unit,
      amount,
      resourceCode: rateInfo.resourceCode,
    };
  });
}
